import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
  BREAKPOINTS_WIDTHS,
  useModal,
  useWindowSize,
  PATHS,
} from '@belong/common';
import {
  Button,
  Form,
  Modal,
  Spinner,
  Text,
  TextField,
  Toast,
} from '@belong/ui';
import type { MaintenanceRecurrenceDetailModel } from 'api/models';
import LegacyButton from 'components/Button/Button';
import { BUTTON_TYPES } from 'components/Button/buttonTypes';
import { isAfter } from 'date-fns';
import { API_ERRORS, URL_QUERY_PARAMS } from 'maintenance/constants';
import { STRINGS } from 'maintenance/strings';
import { getDateByStatus } from 'pages/Accounts/Components/maintenance-recurrence/maintenance-recurrence.utils';
import { cancelRecurringProServiceVisit } from 'store/redux/pros/actions';
import { DATE_FORMATS, formatDate } from 'utils/formatDate';

type MaintenanceRecurrentRouteParams = {
  recurrenceType: string;
  subtaskId: string;
};

type CancelSingleVisitFormSchema = {
  cancellationReason: string;
};

type Props = {
  isResident?: boolean;
  isSubtask?: boolean;
  // Nullable to match the hook that ultimately supplies it; every read outside
  // the click handlers below already goes through `?.`.
  task: MaintenanceRecurrenceDetailModel | null;
};

export const MaintenanceRecurrenceHeaderActions = ({
  isResident = false,
  isSubtask = false,
  task,
}: Props) => {
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const { push } = useHistory();
  const { recurrenceType, subtaskId } =
    useParams<MaintenanceRecurrentRouteParams>();
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const [isCancelModalOpen, openCancelModal, closeCancelModal] = useModal();

  const currentSubtask =
    task?.subTasks?.find((subtask) => subtask.uniqueId === subtaskId) || null;
  const isMobile = width < BREAKPOINTS_WIDTHS.SM;
  const isCurrentUserMaintenanceReporter = isResident
    ? task?.maintenanceRecurrence?.requestedBy === 'Resident'
    : task?.maintenanceRecurrence?.requestedBy === 'Homeowner';

  const isEditionEnabled = isCurrentUserMaintenanceReporter && !isSubtask;
  const isCancellationForSingleVisitEnabled =
    isSubtask && isCurrentUserMaintenanceReporter;

  const cancelSingleVisitInitialValues: CancelSingleVisitFormSchema = {
    cancellationReason: '',
  };

  const handleEditClick = () => {
    const baseURL = isResident
      ? PATHS.RESIDENT_PRO_SERVICE_CREATE
      : PATHS.HOMEOWNER_PRO_SERVICE_CREATE;
    const proServiceCreationPage = baseURL.replace(
      ':proService',
      recurrenceType,
    );

    push({
      pathname: proServiceCreationPage,
      // Both reads here are asserted rather than optional-chained: these
      // handlers only fire from controls that a rendered task put on screen,
      // and skipping the work instead of throwing would silently do nothing.
      search: `?unit-id=${task!.home.unitId}`,
    });
  };

  const validateCancelThisVisitOnly = ({
    cancellationReason,
  }: CancelSingleVisitFormSchema) => {
    const errors: any = {};

    if (!cancellationReason) {
      return (errors.cancellationReason = 'Required');
    }

    return errors;
  };

  const handleCancelThisVisitOnlySubmit = async ({
    cancellationReason,
  }: CancelSingleVisitFormSchema) => {
    try {
      setIsCancelling(true);
      setIsToastVisible(false);

      const nextProServiceAppointment = task!.subTasks.find(
        (subtask) =>
          subtask.uniqueId !== subtaskId &&
          ['OccurringAround', 'Scheduled'].includes(subtask.subTaskStatus) &&
          isAfter(
            // Both dates are optional on the model; `new Date(undefined)` and
            // `new Date('')` are both an Invalid Date, so `isAfter` is unchanged.
            new Date(subtask.calculatedDateNextOn ?? ''),
            new Date(currentSubtask?.calculatedDateNextOn ?? ''),
          ),
      );

      const urlSearchParams = new URLSearchParams();

      urlSearchParams.append(URL_QUERY_PARAMS.TOAST_VISIBLE, 'true');
      urlSearchParams.append(URL_QUERY_PARAMS.SUCCESS, 'true');
      urlSearchParams.append(URL_QUERY_PARAMS.CANCEL, 'true');
      urlSearchParams.append(URL_QUERY_PARAMS.TASK, recurrenceType);

      if (nextProServiceAppointment) {
        urlSearchParams.append(
          URL_QUERY_PARAMS.NEXT_ON,
          nextProServiceAppointment.calculatedDateNextOn ?? '',
        );
      }

      await dispatch(
        cancelRecurringProServiceVisit({
          maintenanceUniqueId: subtaskId,
          resultReason: cancellationReason,
        }),
      );

      const baseURL = isResident
        ? PATHS.RESIDENTS_ACCOUNT_RECURRING_MAINTENANCE
        : PATHS.HOMEOWNER_ACCOUNT_RECURRING_MAINTENANCE;

      push({
        pathname: `${baseURL}/${task!.home.unitId}/${recurrenceType}`,
        search: urlSearchParams.toString(),
      });
    } catch (error: any) {
      console.error(error);

      if (error?.[0]?.errorCode === API_ERRORS.APPOINTMENT_TOO_SOON) {
        setIsToastVisible(true);
      }
    } finally {
      setIsCancelling(false);
    }
  };

  const handleToastClose = () => setIsToastVisible(false);

  const handleCancelModalClose = () => {
    setIsToastVisible(false);
    closeCancelModal();
  };

  const subtaskNextDate = currentSubtask
    ? formatDate(
        getDateByStatus(currentSubtask),
        DATE_FORMATS.DOTTED_DAY_MEDIUM,
      )
    : '';

  return (
    <>
      {isCancelling && <Spinner />}
      <div className="flex items-center">
        {isEditionEnabled && (
          <LegacyButton
            className="flex w-full justify-start px-sm"
            label="EDIT"
            buttonType={BUTTON_TYPES.TEXT}
            onClick={handleEditClick}
          />
        )}

        {isCancellationForSingleVisitEnabled && (
          <LegacyButton
            className="flex w-full justify-start px-sm"
            label="SKIP THIS VISIT"
            buttonType={BUTTON_TYPES.TEXT}
            onClick={openCancelModal}
          />
        )}
      </div>
      <Modal
        isOpen={isCancelModalOpen}
        name="cancel_recurring_visit"
        onDismiss={handleCancelModalClose}
        size={isMobile ? 'fullScreen' : 'auto'}
        overlayClassName="z-fullscreen"
        contentClassName="sm:w-[730px] md:w-[730px]"
      >
        <div className="flex flex-col px-2sm pt-3xl sm:px-lg sm:pt-2xl">
          {isCancelling && <Spinner />}
          <div className="mb-2xl max-w-[264px] self-center whitespace-normal text-center sm:max-w-none">
            <Text variant="h2" fontWeight="semibold">
              Are you sure you want to skip
              <br />
              {`${subtaskNextDate || 'this'}\u00a0visit?`}
            </Text>
          </div>
          <Form
            initialValues={cancelSingleVisitInitialValues}
            onSubmit={handleCancelThisVisitOnlySubmit}
            validate={validateCancelThisVisitOnly}
          >
            {() => (
              <div className="flex flex-col items-center gap-sm pb-xl">
                <div className="mb-md self-stretch">
                  <TextField label="Reason" name="cancellationReason" />
                </div>
                <Form.SubmitButton>Yes, Skip It</Form.SubmitButton>
                <Button
                  variant="text"
                  buttonType="danger"
                  onClick={handleCancelModalClose}
                >
                  {skipCancelText}
                </Button>
              </div>
            )}
          </Form>
        </div>
      </Modal>
      <Toast
        isVisible={isToastVisible}
        onClose={handleToastClose}
        variant="danger"
      >
        <div className="sm:max-w-[480px]">
          {STRINGS['appointment-too-soon']}
        </div>
      </Toast>
    </>
  );
};

const skipCancelText = "NO, DON'T SKIP IT";
